import React from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    CircularProgress,
    Alert,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

const AssociationTable = ({
    data = [],
    config,
    loading,
    error,
    projectId
}) => {
    const columns = config.columns || [];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (data.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                No {config.name.toLowerCase()} found.
            </Box>
        );
    }

    return (
        <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
                <TableRow>
                    {columns.map((col, idx) => (
                        <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                            {col.label}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((record, rowIdx) => {
                    const rowKey = config.getRowKey(record, rowIdx);
                    return (
                        <TableRow key={rowKey} hover>
                            {columns.map((col, colIdx) => {
                                const value = record[col.field];
                                let displayValue = value;

                                // Custom render function
                                if (col.render) {
                                    displayValue = col.render(value, record);
                                } else if (col.renderSessionLinks && Array.isArray(value) && value.length > 0) {
                                    // Render array of session IDs as links (max 15)
                                    const MAX_SESSIONS_DISPLAY = 15;
                                    const sessionsToShow = value.slice(0, MAX_SESSIONS_DISPLAY);
                                    const remainingCount = value.length - MAX_SESSIONS_DISPLAY;

                                    displayValue = (
                                        <>
                                            {sessionsToShow.map((sessionId, idx) => (
                                                <span key={sessionId}>
                                                    <Link
                                                        to={`/projects/${projectId}/hart/sessions/${sessionId}`}
                                                        style={{ color: 'inherit', textDecoration: 'none' }}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                color: 'info.light',
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                        >
                                                            {sessionId}
                                                        </Typography>
                                                    </Link>
                                                    {idx < sessionsToShow.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                            {remainingCount > 0 && (
                                                <Typography
                                                    component="span"
                                                    sx={{ color: 'text.secondary', ml: 0.5 }}
                                                >
                                                    (+{remainingCount} more)
                                                </Typography>
                                            )}
                                        </>
                                    );
                                } else if (value === null || value === undefined) {
                                    displayValue = '—';
                                } else if (typeof value === 'boolean') {
                                    displayValue = value ? 'Yes' : 'No';
                                } else if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
                                    // Format dates
                                    try {
                                        const date = new Date(value);
                                        displayValue = date.toLocaleDateString();
                                    } catch (e) {
                                        displayValue = value;
                                    }
                                }

                                // Handle links
                                if (config.detailRoute && col.linkField) {
                                    const linkId = record[col.linkField];
                                    if (linkId) {
                                        const linkPath = config.detailRoute
                                            .replace(':projectId', projectId || '')
                                            .replace(`:${col.linkField}`, linkId);
                                        displayValue = (
                                            <Link to={linkPath} style={{ color: 'inherit', textDecoration: 'none' }}>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        color: 'info.light',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {displayValue}
                                                </Typography>
                                            </Link>
                                        );
                                    }
                                }

                                return (
                                    <TableCell key={colIdx}>
                                        {displayValue}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

AssociationTable.propTypes = {
    data: PropTypes.array,
    config: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    projectId: PropTypes.string,
};

export default AssociationTable;
